import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOfficeIcon, 
  LinkIcon, 
  CalendarIcon, 
  ClockIcon,
  PencilIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import LeadsService from '../services/leadsService';

const LeadProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [newFollowUp, setNewFollowUp] = useState({
    type: 'call',
    summary: '',
    notes: '',
    nextStep: '',
    nextFollowUpDate: '',
    priority: 'medium',
    completed: false
  });

  useEffect(() => {
    const fetchLead = async () => {
      console.log('LeadProfile: Fetching lead with ID:', id);
      try {
        const result = await LeadsService.getLeadById(id);
        console.log('LeadProfile: API result:', result);
        if (result.success) {
          console.log('LeadProfile: Setting lead data:', result.data);
          setLead(result.data);
        } else {
          console.error('Failed to load lead:', result.error);
        }
      } catch (error) {
        console.error('Error loading lead:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLead();
    } else {
      console.error('LeadProfile: No ID provided');
      setLoading(false);
    }
  }, [id]);

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    console.log('Follow-up form submitted with data:', newFollowUp);
    if (!lead) {
      console.error('No lead data available for follow-up');
      return;
    }

    try {
      const updatedLead = {
        ...lead,
        followUps: [
          ...(lead.followUps || []),
          {
            ...newFollowUp,
            date: new Date().toISOString(),
            nextFollowUpDate: newFollowUp.nextFollowUpDate || null
          }
        ]
      };

      const result = await LeadsService.updateLead(lead._id, updatedLead);
      if (result.success) {
        setLead(result.data);
        setNewFollowUp({
          type: 'call',
          summary: '',
          notes: '',
          nextStep: '',
          nextFollowUpDate: '',
          priority: 'medium',
          completed: false
        });
        setShowFollowUpForm(false);
      }
    } catch (error) {
      console.error('Error adding follow-up:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getSourceIcon = (source) => {
    const icons = {
      'website': '🌐',
      'instagram': '📷',
      'facebook': '📘',
      'whatsapp': '💬',
      'linkedin': '💼',
      'twitter': '🐦',
      'youtube': '📺',
      'referral': '👥',
      'email_campaign': '📧',
      'phone_call': '📞'
    };
    return icons[source] || '📍';
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-yellow-100 text-yellow-800',
      'contacted': 'bg-blue-100 text-blue-800',
      'qualified': 'bg-green-100 text-green-800',
      'proposal': 'bg-purple-100 text-purple-800',
      'negotiation': 'bg-orange-100 text-orange-800',
      'closed_won': 'bg-green-100 text-green-800',
      'closed_lost': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">Lead not found</h2>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center mx-auto"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{lead.name || 'Unknown Lead'}</h1>
                {lead.title && (
                  <div className="mt-2 mb-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      📋 {lead.title}
                    </span>
                  </div>
                )}
                <p className="text-sm text-gray-500">
                  {lead.company && `${lead.company} • `}
                  Lead ID: {lead._id?.slice(-8) || 'N/A'}
                </p>
                {/* Assigned Employee Information */}
                {lead.assignedEmployeeName && (
                  <div className="mt-2 flex items-center space-x-4">
                    <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                      <UserIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-800">
                        Assigned to: {lead.assignedEmployeeName}
                      </span>
                    </div>
                    {lead.assignedEmployeePhone && (
                      <div className="flex items-center bg-green-50 px-3 py-1 rounded-full">
                        <PhoneIcon className="h-4 w-4 text-green-600 mr-2" />
                        <a 
                          href={`tel:${lead.assignedEmployeePhone}`} 
                          className="text-sm font-medium text-green-800 hover:text-green-900 hover:underline"
                        >
                          {lead.assignedEmployeePhone}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead.status || 'new')}`}>
                {(lead.status || 'new').replace('_', ' ').toUpperCase()}
              </span>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Edit Lead
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Sidebar - Lead Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{lead.email || 'N/A'}</p>
                    <p className="text-xs text-gray-500">Email</p>
                  </div>
                </div>
                {lead.phone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{lead.phone}</p>
                      <p className="text-xs text-gray-500">Phone</p>
                    </div>
                  </div>
                )}
                {lead.company && (
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{lead.company}</p>
                      <p className="text-xs text-gray-500">Company</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Lead Source */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Source</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{getSourceIcon(lead.source)}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 capitalize">{lead.source}</p>
                    <p className="text-xs text-gray-500">Source Platform</p>
                  </div>
                </div>
                {lead.sourceDetails && (
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-xs text-gray-600">{lead.sourceDetails}</p>
                  </div>
                )}
                {lead.autoCaptured && (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs">Auto Captured</span>
                  </div>
                )}
              </div>
            </div>

            {/* Assignment Information */}
            {lead.assignedEmployeeName && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Assignment Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{lead.assignedEmployeeName}</p>
                      <p className="text-xs text-gray-500">Assigned Employee</p>
                    </div>
                  </div>
                  
                  {lead.assignedEmployeePhone && (
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-green-600 mr-3" />
                      <div className="flex-1">
                        <a 
                          href={`tel:${lead.assignedEmployeePhone}`} 
                          className="text-sm font-medium text-green-700 hover:text-green-900 hover:underline"
                        >
                          {lead.assignedEmployeePhone}
                        </a>
                        <p className="text-xs text-gray-500">Contact Number</p>
                      </div>
                    </div>
                  )}
                  
                  {lead.assignedEmployeeEmail && (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <a 
                          href={`mailto:${lead.assignedEmployeeEmail}`} 
                          className="text-sm font-medium text-purple-700 hover:text-purple-900 hover:underline"
                        >
                          {lead.assignedEmployeeEmail}
                        </a>
                        <p className="text-xs text-gray-500">Email Address</p>
                      </div>
                    </div>
                  )}
                  
                  {lead.assignedAt && (
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{formatDate(lead.assignedAt)}</p>
                        <p className="text-xs text-gray-500">Assignment Date</p>
                      </div>
                    </div>
                  )}
                  
                  {lead.assignmentNotes && (
                    <div className="bg-gray-50 rounded-md p-3">
                      <p className="text-xs text-gray-600">{lead.assignmentNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lead Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <div>
                    <p className="font-medium">Lead Date</p>
                    <p className="text-gray-500">{formatDateOnly(lead.date)}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm">
                  <CalendarIcon className="h-4 w-4 text-green-400 mr-2" />
                  <div>
                    <p className="font-medium">Created</p>
                    <p className="text-gray-500">{formatDate(lead.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm">
                  <ClockIcon className="h-4 w-4 text-blue-400 mr-2" />
                  <div>
                    <p className="font-medium">Last Contact</p>
                    <p className="text-gray-500">{formatDate(lead.lastContact)}</p>
                  </div>
                </div>
                {lead.nextFollowUp && (
                  <div className="flex items-center text-sm">
                    <ClockIcon className="h-4 w-4 text-orange-400 mr-2" />
                    <div>
                      <p className="font-medium">Next Follow-up</p>
                      <p className="text-orange-600 font-medium">{formatDate(lead.nextFollowUp)}</p>
                    </div>
                  </div>
                )}
                {lead.assignedEmployeeName && (
                  <div className="flex items-center text-sm">
                    <UserIcon className="h-4 w-4 text-purple-400 mr-2" />
                    <div>
                      <p className="font-medium">Assigned To</p>
                      <p className="text-gray-500">{lead.assignedEmployeeName}</p>
                      {lead.assignedEmployeePhone && (
                        <a 
                          href={`tel:${lead.assignedEmployeePhone}`} 
                          className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                        >
                          📞 {lead.assignedEmployeePhone}
                        </a>
                      )}
                      {lead.assignedAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          Assigned: {formatDate(lead.assignedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Lead Statistics */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Statistics</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Serial Number</span>
                  <span className="text-sm font-medium text-gray-900">#{lead.serialNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Follow-ups</span>
                  <span className="text-sm font-medium text-blue-600">{lead.followUps?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Lead Type</span>
                  <span className="text-sm font-medium text-gray-900">{lead.autoCaptured ? 'Auto Captured' : 'Manual Entry'}</span>
                </div>
                {lead.productOfInterest && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Product Interest</span>
                    <span className="text-sm font-medium text-purple-600">{lead.productOfInterest}</span>
                  </div>
                )}
                {lead.sector && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sector</span>
                    <span className="text-sm font-medium text-green-600">{lead.sector}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Content - Tabs */}
          <div className="lg:col-span-2">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6">
                  {['overview', 'followups', 'history'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                        activeTab === tab
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab === 'followups' ? 'Follow-ups' : tab}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Lead Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Priority</label>
                          <p className="text-sm text-gray-900 capitalize">{lead.priority}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Value</label>
                          <p className="text-sm text-gray-900">₹{lead.value?.toLocaleString() || 0}</p>
                        </div>
                        {lead.budget && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Budget</label>
                            <p className="text-sm text-gray-900 bg-green-50 px-2 py-1 rounded inline-block">
                              💰 {lead.budget}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className="text-sm font-medium text-gray-500">Lead Date</label>
                          <p className="text-sm text-gray-900">{formatDateOnly(lead.date)}</p>
                        </div>
                        {lead.productOfInterest && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Product of Interest</label>
                            <p className="text-sm text-gray-900">{lead.productOfInterest}</p>
                          </div>
                        )}
                        {lead.sector && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Sector</label>
                            <p className="text-sm text-gray-900">{lead.sector}</p>
                          </div>
                        )}
                        {lead.productCategory && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Product Category</label>
                            <p className="text-sm text-gray-900">{lead.productCategory}</p>
                          </div>
                        )}
                        {lead.address && (
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-500">Address</label>
                            <p className="text-sm text-gray-900">{lead.address}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Custom Fields Display */}
                    {Object.keys(lead).filter(key => 
                      !['_id', 'customerName', 'name', 'email', 'phone', 'contactNumber', 'customerCompany', 'company', 'source', 'sourceOfLead', 'status', 'priority', 'value', 'budget', 'notes', 'address', 'productOfInterest', 'sector', 'productCategory', 'date', 'createdAt', 'updatedAt', 'lastContact', 'followUps', 'assignedTo', 'assignedAt', 'assignmentNotes', 'assignedEmployeeName', 'assignedEmployeePhone', 'assignedEmployeeEmail', 'userId', 'company', 'companyId', 'autoCaptured', 'captureSource', 'customFields', 'tags', 'socialProfiles', 'nextFollowUp', 'serialNumber', '__v'].includes(key) && 
                      lead[key] && 
                      typeof lead[key] === 'string'
                    ).length > 0 && (
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Additional Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.keys(lead).filter(key => 
                            !['_id', 'customerName', 'name', 'email', 'phone', 'contactNumber', 'customerCompany', 'company', 'source', 'sourceOfLead', 'status', 'priority', 'value', 'budget', 'notes', 'address', 'productOfInterest', 'sector', 'productCategory', 'date', 'createdAt', 'updatedAt', 'lastContact', 'followUps', 'assignedTo', 'assignedAt', 'assignmentNotes', 'assignedEmployeeName', 'assignedEmployeePhone', 'assignedEmployeeEmail', 'userId', 'company', 'companyId', 'autoCaptured', 'captureSource', 'customFields', 'tags', 'socialProfiles', 'nextFollowUp', 'serialNumber', '__v'].includes(key) && 
                            lead[key] && 
                            typeof lead[key] === 'string'
                          ).map(key => (
                            <div key={key}>
                              <label className="text-sm font-medium text-gray-500 capitalize">{key.replace(/_/g, ' ')}</label>
                              <p className="text-sm text-gray-900">{lead[key]}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {lead.notes && (
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Notes</h4>
                        <div className="bg-gray-50 rounded-md p-4">
                          <p className="text-sm text-gray-700">{lead.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'followups' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h4 className="text-lg font-medium text-gray-900">Follow-ups</h4>
                      <button 
                        onClick={() => {
                          console.log('Add Follow-up button clicked');
                          setShowFollowUpForm(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Follow-up
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {lead.followUps && lead.followUps.length > 0 ? (
                        lead.followUps.map((followUp, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  followUp.type === 'call' ? 'bg-blue-100 text-blue-800' :
                                  followUp.type === 'email' ? 'bg-purple-100 text-purple-800' :
                                  followUp.type === 'meeting' ? 'bg-green-100 text-green-800' :
                                  followUp.type === 'demo' ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {followUp.type.charAt(0).toUpperCase() + followUp.type.slice(1)}
                                </span>
                                {followUp.priority && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    followUp.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                    followUp.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    followUp.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {followUp.priority}
                                  </span>
                                )}
                                {followUp.completed && (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(followUp.date)}
                              </span>
                            </div>
                            
                            <div className="mb-3">
                              <h5 className="text-sm font-medium text-gray-900 mb-1">{followUp.summary}</h5>
                              {followUp.notes && (
                                <p className="text-sm text-gray-600">{followUp.notes}</p>
                              )}
                            </div>
                            
                            {followUp.nextStep && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-500">Next Step:</span>
                                <p className="text-xs text-gray-700">{followUp.nextStep}</p>
                              </div>
                            )}
                            
                            {followUp.nextFollowUpDate && (
                              <div className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                Next Follow-up: {formatDate(followUp.nextFollowUpDate)}
                              </div>
                            )}
                            
                            {followUp.addedByName && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-500">
                                  Added by: {followUp.addedByName} {followUp.addedByRole && `(${followUp.addedByRole})`}
                                </span>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No follow-ups yet</p>
                          <p className="text-sm text-gray-400">Click "Add Follow-up" to start tracking interactions</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Activity History</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Lead Created</p>
                          <p className="text-xs text-gray-500">{formatDate(lead.createdAt)}</p>
                        </div>
                      </div>
                      {lead.followUps?.map((followUp, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{followUp.summary}</p>
                            <p className="text-xs text-gray-500">{formatDate(followUp.date)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-up Form Modal */}
      {showFollowUpForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Follow-up</h3>
              <form onSubmit={handleFollowUpSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={newFollowUp.type}
                    onChange={(e) => setNewFollowUp({...newFollowUp, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                    <option value="demo">Demo</option>
                    <option value="proposal">Proposal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary
                  </label>
                  <input
                    type="text"
                    value={newFollowUp.summary}
                    onChange={(e) => setNewFollowUp({...newFollowUp, summary: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief summary of follow-up"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newFollowUp.notes}
                    onChange={(e) => setNewFollowUp({...newFollowUp, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Detailed notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newFollowUp.priority}
                    onChange={(e) => setNewFollowUp({...newFollowUp, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Step
                  </label>
                  <input
                    type="text"
                    value={newFollowUp.nextStep}
                    onChange={(e) => setNewFollowUp({...newFollowUp, nextStep: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="What's the next action?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Follow-up Date
                  </label>
                  <input
                    type="datetime-local"
                    value={newFollowUp.nextFollowUpDate}
                    onChange={(e) => setNewFollowUp({...newFollowUp, nextFollowUpDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="completed"
                    checked={newFollowUp.completed}
                    onChange={(e) => setNewFollowUp({...newFollowUp, completed: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="completed" className="ml-2 block text-sm text-gray-900">
                    Mark as completed
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowFollowUpForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Add Follow-up
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadProfile;
